
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Header } from '../../components/Header';
import {
  Search, FilterList, Visibility, Download,
  Inventory2, Verified, AccountBalanceWallet, Gavel, VerifiedUser,
  FolderSpecial, ChevronLeft, ChevronRight
} from '@mui/icons-material';

export const CopyrightDashboardScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);

  const stats = [
    { label: t('copyright.protected'), value: '142', icon: <Inventory2 className="text-primary" />, bg: 'bg-primary/10' },
    { label: t('copyright.certified'), value: '142', icon: <Verified className="text-emerald-600" />, bg: 'bg-emerald-500/10' },
    { label: t('copyright.onChain'), value: '1,452', icon: <AccountBalanceWallet className="text-amber-600" />, bg: 'bg-amber-500/10' },
    { label: t('copyright.ready'), value: '100%', icon: <Gavel className="text-blue-600" />, bg: 'bg-blue-500/10' },
  ];

  const assets = [
    {
      id: 1,
      image: 'https://lh3.googleusercontent.com/aida-public/AB6AXuDCQMzjAyWih2sPbO12BgRqpCP6yiRTRNNT6lmVRvNebRoc3UkTHRUoypoDTpY2YtrvAkMddy2njzTyfipPZ5yR1DdBzWaecoTtIZNHKlW7oiGg5CYbqEtdqbyFawgKePNJeeUnAsNKkJNqS6wV8QrjRh-Cioc8E5yL_8ADVBOKHzwBLcPVmTpOgehYeAflzKCInBSMfI29Eefcd-1oRixqvU4oeVakSwzvEcOU5qipsypB6t0t9NuZasdrbOwg1kf2sKawHVDgQcAV',
      name: 'Luxury Watch Model X',
      sku: 'LW-2023-OZON',
      platform: 'Ozon',
      synced: '2023-10-24',
      bundleId: '8832'
    },
    {
      id: 2,
      image: 'https://lh3.googleusercontent.com/aida-public/AB6AXuBz4lgnxY3_Ps1XFUH2g3Pw9TBXdmlinRMon3t_pEupP75h9r1CakPOtqqJGVyXYI_RayPfQe9SyBvhlwYPehFJM-_J74BtVkHFjacgTk3GX2SFj9FNnIZG_nXB2iOMNZSc6LLK8NKgqUIPfRcN74AGmc1ncIRwoMro6fbzZ6aweA6L59gpgUvLCbYy8lyAsfhD9u1k6Uczzcs8V8aZqAGG5thLk2ChjcZl1Nj9o3wQx6e3K8gY3cjdBESaUwliAY2xlZ0adhrWyBTn',
      name: 'Designer Handbag 2024',
      sku: 'HB-AMZ-PR',
      platform: 'Amazon',
      synced: '2023-10-22',
      bundleId: '8831'
    }
  ];

  const toggleSelect = (id: number) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter(i => i !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    alert("Downloading legal bundle...");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-32">
      <Header activeNav="copyright" maxWidth="max-w-6xl" />

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
          <div className="space-y-1">
            <h2 className="text-3xl font-black tracking-tight text-slate-900">{t('copyright.title')}</h2>
            <p className="text-slate-500">{t('copyright.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-100 rounded-lg">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
            </span>
            <span className="text-sm font-bold text-primary uppercase tracking-wider">{t('copyright.statusActive')}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats.map((stat, i) => (
            <div key={i} className="bg-white p-6 rounded-xl border border-slate-200 flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow">
              <div className={`p-3 ${stat.bg} rounded-lg`}>
                {stat.icon}
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{stat.label}</p>
                <p className="text-2xl font-black text-slate-900 leading-none">{stat.value}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-200 flex items-center justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" fontSize="small" />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border-none rounded-xl focus:ring-2 focus:ring-primary text-sm transition-all outline-none"
                placeholder={t('copyright.searchPlaceholder')}
                type="text"
              />
            </div>
            <button
                onClick={() => setFilterActive(!filterActive)}
                className={`flex items-center gap-2 px-4 py-2.5 border rounded-xl text-sm font-semibold transition-all ${filterActive ? 'bg-primary text-white border-primary' : 'bg-slate-50 border-slate-200 hover:bg-slate-100 text-slate-700'}`}
            >
              <FilterList fontSize="small" />
              {t('copyright.filter')}
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-200">
                  <th className="px-6 py-4 w-12 text-center">
                    <input
                        type="checkbox"
                        checked={selectedItems.length === assets.length}
                        onChange={() => {
                            if (selectedItems.length === assets.length) setSelectedItems([]);
                            else setSelectedItems(assets.map(a => a.id));
                        }}
                        className="rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
                    />
                  </th>
                  <th className="px-4 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">{t('copyright.table.preview')}</th>
                  <th className="px-4 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">{t('copyright.table.product')}</th>
                  <th className="px-4 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">{t('copyright.table.status')}</th>
                  <th className="px-4 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">{t('copyright.table.bundle')}</th>
                  <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">{t('copyright.table.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {assets.map((asset) => (
                  <tr key={asset.id} className="hover:bg-slate-50/80 transition-colors group">
                    <td className="px-6 py-6 text-center">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(asset.id)}
                        onChange={() => toggleSelect(asset.id)}
                        className="rounded border-slate-300 text-primary cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-6">
                      <div className="relative block h-14 w-14 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden cursor-zoom-in" onClick={() => navigate('/copyright/letter')}>
                        <img alt="Product preview" className="w-full h-full object-cover" src={asset.image} />
                      </div>
                    </td>
                    <td className="px-4 py-6">
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-slate-900">{asset.name}</span>
                        <span className="text-[11px] text-slate-500 font-medium">SKU: {asset.sku} • {asset.platform}</span>
                      </div>
                    </td>
                    <td className="px-4 py-6">
                      <div className="flex flex-col gap-1.5">
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-emerald-50 text-emerald-600 border border-emerald-100 w-fit">
                          <VerifiedUser style={{ fontSize: 12 }} />
                          {t('copyright.table.certified')}
                        </span>
                        <span className="text-[10px] text-slate-400 font-medium">{t('copyright.table.synced')}{asset.synced}</span>
                      </div>
                    </td>
                    <td className="px-4 py-6">
                      <div className="flex items-center gap-3 p-2 rounded-xl border border-slate-100 hover:border-primary/30 hover:bg-primary/5 transition-all w-fit cursor-pointer" onClick={() => navigate('/copyright/letter')}>
                        <div className="h-10 w-10 flex items-center justify-center bg-primary/10 rounded-lg text-primary">
                          <FolderSpecial />
                        </div>
                        <div className="flex flex-col pr-4">
                          <span className="text-[11px] font-bold text-slate-900 leading-tight">Bundle #{asset.bundleId}</span>
                          <span className="text-[10px] text-slate-500">5 {t('copyright.table.docs')}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-6">
                      <div className="flex items-center justify-end gap-3">
                        <button onClick={() => navigate('/copyright/letter')} className="flex items-center gap-2 px-4 py-2.5 bg-white text-slate-700 border border-slate-200 rounded-xl text-xs font-bold hover:bg-slate-50 transition-all">
                          <Visibility style={{ fontSize: 18 }} />
                          {t('copyright.table.previewBtn')}
                        </button>
                        <button onClick={handleDownload} className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl text-xs font-bold hover:bg-blue-700 transition-all shadow-md shadow-primary/20 active:scale-95">
                          <Download style={{ fontSize: 18 }} />
                          {t('copyright.table.download')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-5 flex items-center justify-between border-t border-slate-200">
            <span className="text-xs font-medium text-slate-500">Showing 1 to 2 of 142 protected assets</span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                className="p-2 rounded hover:bg-slate-100 text-slate-400 disabled:opacity-30 disabled:hover:bg-transparent"
                disabled={currentPage === 1}
              >
                <ChevronLeft fontSize="small" />
              </button>
              {[1, 2, 3].map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`h-8 w-8 text-xs font-bold rounded-lg transition-colors ${currentPage === page ? 'bg-primary text-white' : 'text-slate-500 hover:bg-slate-100'}`}
                  >
                    {page}
                  </button>
              ))}
              <button
                onClick={() => setCurrentPage(Math.min(3, currentPage + 1))}
                className="p-2 rounded hover:bg-slate-100 text-slate-400 disabled:opacity-30 disabled:hover:bg-transparent"
                disabled={currentPage === 3}
              >
                <ChevronRight fontSize="small" />
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
